/**
 * Offline Storage Service using IndexedDB
 * 
 * This service stores article content for offline reading.
 * Works on both web browsers and Android PWAs.
 */

const DB_NAME = 'linkflare_offline_storage'
const DB_VERSION = 1
const ARTICLES_STORE = 'articles'

class OfflineStorageService {
  constructor() {
    this.db = null
  }

  /**
   * Initialize the IndexedDB database
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result

        // Create articles store if it doesn't exist
        if (!db.objectStoreNames.contains(ARTICLES_STORE)) {
          const articlesStore = db.createObjectStore(ARTICLES_STORE, { keyPath: 'linkId' })
          articlesStore.createIndex('timestamp', 'timestamp', { unique: false })
          articlesStore.createIndex('title', 'title', { unique: false })
          console.log('Created articles object store')
        }
      }
    })
  }

  /**
   * Ensure database is initialized
   */
  async ensureDb() {
    if (!this.db) {
      await this.init()
    }
    return this.db
  }

  /**
   * Save an article for offline reading
   * @param {number} linkId - The link ID
   * @param {object} articleData - Article data containing title and html
   * @returns {Promise<void>}
   */
  async saveArticle(linkId, articleData) {
    try {
      const db = await this.ensureDb()
      
      const article = {
        linkId,
        title: articleData.title,
        html: articleData.html,
        timestamp: Date.now(),
        url: articleData.url || null
      }

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ARTICLES_STORE], 'readwrite')
        const store = transaction.objectStore(ARTICLES_STORE)
        const request = store.put(article)

        request.onsuccess = () => {
          console.log(`Article ${linkId} saved for offline reading`)
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to save article:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error saving article:', error)
      throw error
    }
  }

  /**
   * Get an article from offline storage
   * @param {number} linkId - The link ID
   * @returns {Promise<object|null>} Article data or null if not found
   */
  async getArticle(linkId) {
    try {
      const db = await this.ensureDb()

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ARTICLES_STORE], 'readonly')
        const store = transaction.objectStore(ARTICLES_STORE)
        const request = store.get(linkId)

        request.onsuccess = () => {
          resolve(request.result || null)
        }

        request.onerror = () => {
          console.error('Failed to get article:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error getting article:', error)
      return null
    }
  }

  /**
   * Check if an article is saved offline
   * @param {number} linkId - The link ID
   * @returns {Promise<boolean>}
   */
  async hasArticle(linkId) {
    const article = await this.getArticle(linkId)
    return article !== null
  }

  /**
   * Delete an article from offline storage
   * @param {number} linkId - The link ID
   * @returns {Promise<void>}
   */
  async deleteArticle(linkId) {
    try {
      const db = await this.ensureDb()

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ARTICLES_STORE], 'readwrite')
        const store = transaction.objectStore(ARTICLES_STORE)
        const request = store.delete(linkId)

        request.onsuccess = () => {
          console.log(`Article ${linkId} deleted from offline storage`)
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to delete article:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error deleting article:', error)
      throw error
    }
  }

  /**
   * Get all saved articles
   * @returns {Promise<Array>}
   */
  async getAllArticles() {
    try {
      const db = await this.ensureDb()

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ARTICLES_STORE], 'readonly')
        const store = transaction.objectStore(ARTICLES_STORE)
        const request = store.getAll()

        request.onsuccess = () => {
          resolve(request.result || [])
        }

        request.onerror = () => {
          console.error('Failed to get all articles:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error getting all articles:', error)
      return []
    }
  }

  /**
   * Get the count of saved articles
   * @returns {Promise<number>}
   */
  async getArticleCount() {
    try {
      const db = await this.ensureDb()

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ARTICLES_STORE], 'readonly')
        const store = transaction.objectStore(ARTICLES_STORE)
        const request = store.count()

        request.onsuccess = () => {
          resolve(request.result || 0)
        }

        request.onerror = () => {
          console.error('Failed to count articles:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error counting articles:', error)
      return 0
    }
  }

  /**
   * Clear all saved articles
   * @returns {Promise<void>}
   */
  async clearAll() {
    try {
      const db = await this.ensureDb()

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ARTICLES_STORE], 'readwrite')
        const store = transaction.objectStore(ARTICLES_STORE)
        const request = store.clear()

        request.onsuccess = () => {
          console.log('All offline articles cleared')
          resolve()
        }

        request.onerror = () => {
          console.error('Failed to clear articles:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Error clearing articles:', error)
      throw error
    }
  }

  /**
   * Get storage size estimate (in bytes)
   * @returns {Promise<object>} Storage estimate with usage and quota
   */
  async getStorageEstimate() {
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate()
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0,
          usageInMB: ((estimate.usage || 0) / (1024 * 1024)).toFixed(2),
          quotaInMB: ((estimate.quota || 0) / (1024 * 1024)).toFixed(2),
          percentUsed: estimate.quota ? ((estimate.usage / estimate.quota) * 100).toFixed(2) : 0
        }
      } catch (error) {
        console.error('Error getting storage estimate:', error)
        return null
      }
    }
    return null
  }
}

export default new OfflineStorageService()
